import React, { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
    Form,
    FormFields,
    FormActions,
    FormDropdown,
    mapToDropdownOption,
    ValidationResult,
    FormValidationSummary,
    useNotifications
} from "sprint-common-web";

import { LanguagesDataContext } from "../../../../../../common/data/lookup-data-providers/LanguagesDataProvider";
import { useOnPremAssetDetails } from "../../../common/hooks/useOnPremAssetDetails";
import { useEditOnPremSubtitleAssetLanguage } from "./useEditOnPremSubtitleAssetLanguage";

export interface VideoSubtitleFormProps {
    onDismiss(): void;
    onSubmit(newAssetId: string): void;
    subtitleId: string;
}

interface VideoSubtitleFormData {
    subtitleId: string;
}

export const OnPremSubtitleAssetLanguageForm = ({
    onDismiss,
    onSubmit,
    subtitleId
}: VideoSubtitleFormProps): JSX.Element => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [validationResult, setValidationResult] = useState<ValidationResult>();
    const { subtitleLanguages } = useContext(LanguagesDataContext);
    const { success, error } = useNotifications();
    const { data: assetDetails } = useOnPremAssetDetails();
    const {
        validateOnPremSubtitleAssetLanguage,
        updateOnPremSubtitleAssetLanguage
    } = useEditOnPremSubtitleAssetLanguage({
        invalidationKey: assetDetails?.assetId
    });

    const { handleSubmit, errors, control, formState } = useForm<VideoSubtitleFormData>({
        defaultValues: {
            subtitleId
        }
    });

    const handleSubtitleUpdate = async ({ subtitleId: subtitleLanguageId }: VideoSubtitleFormData) => {
        setIsLoading(true);

        try {
            const result = await validateAndSetResult(subtitleLanguageId);

            if (!result?.isValid) {
                setIsLoading(false);
                return;
            }

            if (!assetDetails) {
                return;
            }

            const updateResponse = await updateOnPremSubtitleAssetLanguage.mutateAsync({
                assetId: assetDetails.assetId,
                subtitleLanguageId
            });

            onSubmit(updateResponse.newAssetId);

            success("Subtitle language updated successfully.");
        } catch (e) {
            error("Failed to update subtitle language.");
        } finally {
            setIsLoading(false);
        }
    };

    const validateAndSetResult = async (subtitleLanguageId: string) => {
        if (!assetDetails) {
            return;
        }

        const result = await validateOnPremSubtitleAssetLanguage.mutateAsync({
            assetId: assetDetails.assetId,
            subtitleLanguageId
        });

        setValidationResult(result);

        return result;
    };

    return (
        <Form onSubmit={handleSubmit(handleSubtitleUpdate)}>
            <FormFields>
                <FormDropdown
                    options={subtitleLanguages.map((dub) => mapToDropdownOption(dub))}
                    label="Subtitle"
                    name="subtitleId"
                    control={control}
                    errors={errors}
                    disableClear={true}
                    disabled={!subtitleLanguages?.length}
                />
                {validationResult && <FormValidationSummary validationResult={validationResult} />}
                <FormActions
                    onCancel={onDismiss}
                    isLoading={formState.isValidating || formState.isSubmitting || isLoading}
                    submitDisabled={!formState.isDirty}
                />
            </FormFields>
        </Form>
    );
};
